import React from "react";
import { Box, Typography, Button, Container, Grid, useMediaQuery, useTheme, Divider } from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Assets
import heroBg from "../assets/nextix/final-background.jpg";
import businessIdentity from "../assets/nextix/business-identity-1.jpg";
import businessCloud from "../assets/nextix/business-cloud.jpg";
import businessFintech from "../assets/nextix/business-fintech.jpg";
import businessAuth from "../assets/nextix/business-authentication.jpg";
import businessInfra from "../assets/nextix/business-digital.jpg";
import learnMoreImg from "../assets/nextix/learnmore-2.jpg";
import heroBg2 from "../assets/downloaded/abc.jpg";

// Client Logos
import comelec from "../assets/nextix/comelec.jpg";
import dbm from "../assets/nextix/dbm.jpg";
import lto from "../assets/nextix/lto.jpg";
import intellicare from "../assets/nextix/intellicare-2.jpg";
import mitsubishi from "../assets/nextix/mitsubishi-motors-2.jpg";
import toyota from "../assets/nextix/toyota-logo.jpg";
import jollibee from "../assets/nextix/jollibee-logo.jpg";
import aboitiz from "../assets/nextix/aboitizland-logo.jpg";
import pldtClient from "../assets/downloaded/p-pldt-logo-300x150.jpg";
import pnb from "../assets/nextix/pnb-logo.jpg";
import lufthansa from "../assets/nextix/lufthansatechnik-logo.jpg";

// Partner Logos
import thales from "../assets/downloaded/partners-thales-300x150.jpg";
import aws from "../assets/downloaded/partners-aws-300x150.jpg";
import microsoft from "../assets/downloaded/microsoft-partner-300x150.jpg";
import pldtPartner from "../assets/downloaded/p-pldt-logo-300x150.jpg";
import ovcode from "../assets/downloaded/partners-ovcode-300x150.jpg";

const BusinessCard = ({ image, link, index }) => (

    <Box
        component={Link}
        to={link}
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            textDecoration: "none",
        }}
    >
        <Box
            component="img"
            src={image}
            alt=""
            sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
            }}
        />
    </Box>
);

const LandingScreen = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <Box sx={{ backgroundColor: "#fff" }}>

            {/* HERO SECTION */}
            <Box sx={{
                position: "relative",
                height: "100vh",
                maxHeight: "100%",
                backgroundImage: `url(${heroBg})`,
                backgroundSize: "cover",
                backgroundPosition: "top",
                backgroundAttachment: isMobile ? "scroll" : "fixed", // Fixed - stays in place while content scrolls over
                display: "flex",
                alignItems: "flex-end", // Position at bottom
                pb: 8// Padding from bottom

            }}>
                <Container maxWidth="lg" sx={{ textAlign: "center" }}>
                    <Box component={motion.div}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        sx={{ color: "white" }}
                    >
                        <Typography variant="h2" sx={{
                            fontWeight: 300,
                            mb: 2,
                            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                            letterSpacing: "0.02em"
                        }}>
                            Building Digital Innovations
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* SECONDARY HERO SECTION - Blue Background */}
            <Box sx={{
                position: "relative",
                py: { xs: 8, md: 12 },
                backgroundImage: `url(${heroBg2})`, // Using one of the business images as background
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundAttachment: isMobile ? "scroll" : "fixed"
            }}>
                {/* Blue overlay */}
                <Box sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(9, 92, 144, 0.5)", // Blue overlay matching original
                }} />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: "center",
                                fontWeight: 300,
                                color: "white",
                                mb: 2,
                                lineHeight: 1.4
                            }}
                        >
                            next|ix is an award winning technology company focused
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: "center",
                                fontWeight: 100,
                                color: "white",
                                mb: 4,
                                lineHeight: 1.4
                            }}
                        >
                            on next generation positive impact digital platforms.
                        </Typography>
                        <Box sx={{ textAlign: "center" }}>
                            <Button
                                variant="outlined"
                                component={Link}
                                to="/who-we-are"
                                sx={{
                                    borderColor: "white",
                                    color: "white",
                                    borderRadius: 0,
                                    padding: "12px 30px",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "1px",
                                    "&:hover": {
                                        borderColor: "#82c9ed",
                                        backgroundColor: "rgba(130, 201, 237, 0.1)"
                                    }
                                }}
                            >
                                Learn More
                            </Button>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* OUR BUSINESSES SECTION */}
            <Box sx={{ py: 8, backgroundColor: "#fff" }}>
                <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Typography variant="h4" sx={{ textAlign: "center", fontWeight: 300, mb: { xs: 2, md: 4 }, color: "#1c1c1c" }}>
                            Our Businesses
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                    </motion.div>
                </Container>

                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 2, sm: 3, md: 5 }}>
                        <Grid item xs={12} sm={6} md={4}>
                            <BusinessCard index={0} image={businessIdentity} link="/business-identity" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <BusinessCard index={1} image={businessCloud} link="/business-cloud" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <BusinessCard index={2} image={businessAuth} link="/business-authentication" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <BusinessCard index={3} image={businessFintech} link="/business-fintech" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <BusinessCard index={4} image={businessInfra} link="/business-digital-infrastructure" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <BusinessCard index={5} image={learnMoreImg} link="/businesses" />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CLIENTS SECTION */}
            <Box sx={{ py: 8, backgroundColor: "#fff" }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Typography variant="h4" sx={{ textAlign: "center", fontWeight: 300, mb: 6, color: "#4c4c4c" }}>
                            Our Clients
                        </Typography>
                    </motion.div>

                    <Grid container spacing={4} justifyContent="center" alignItems="center">
                        {[toyota, pnb, jollibee, aboitiz, lufthansa, intellicare, dbm, lto, comelec, mitsubishi,].map((logo, index) => (
                            <Grid item xs={6} sm={4} md={2.4} key={index} component={motion.div}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                            >
                                <Box
                                    component="img"
                                    src={logo}
                                    alt="client logo"
                                    sx={{
                                        width: "100%",
                                        height: "auto",
                                        maxWidth: "200px",
                                        display: "block",
                                        mx: "auto",
                                        transition: "all 0.3s",
                                        "&:hover": { filter: "grayscale(100%)" }
                                    }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Box sx={{ textAlign: "center", mt: 6 }}>
                        <Button
                            component={Link}
                            to="/who-we-are#clients"
                            sx={{
                                color: "#375ca8",
                                fontWeight: "bold",
                                "&:hover": { color: "#82c9ed", backgroundColor: "transparent" }
                            }}
                        >
                            View more clients
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* OUR PARTNERS SECTION */}
            <Box sx={{ py: 8, backgroundColor: "#fff", borderTop: "1px solid #eee" }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <Typography variant="h4" sx={{ textAlign: "center", fontWeight: 300, mb: 6, color: "#4c4c4c" }}>
                            Our Partners
                        </Typography>
                    </motion.div>
                    <Grid container spacing={6} justifyContent="center" alignItems="center">
                        {[thales, aws, microsoft, pldtPartner, ovcode].map((logo, index) => (
                            <Grid item xs={6} sm={4} md={2.4} key={index} component={motion.div}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Box component="img" src={logo} alt="Partner" sx={{ maxWidth: "100%", height: "auto", transition: "all 0.3s", "&:hover": { filter: "grayscale(100%)" } }} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

        </Box >
    );
};


export default LandingScreen
